/**
 * Centralized Unicode character ranges for the supported languages (en, he, ar).
 * These constants are used for:
 * - Text direction detection (RTL/LTR) in textDirection.ts
 * - Search input validation in listLearningObjects.ts
 *
 * Reference: https://en.wikipedia.org/wiki/Unicode_block
 */

// =============================================================================
// Hebrew Unicode Ranges
// =============================================================================

/**
 * Hebrew script Unicode range (U+0590 to U+05FF)
 * Includes: Hebrew letters, vowels (nikud), cantillation marks
 */
export const UNICODE_HEBREW = '\u0590-\u05FF';

// =============================================================================
// Arabic Unicode Ranges
// =============================================================================

/**
 * Arabic script basic Unicode range (U+0600 to U+06FF)
 * Includes: Arabic letters, Arabic-Indic digits, Arabic punctuation
 */
export const UNICODE_ARABIC_BASIC = '\u0600-\u06FF';

/**
 * Arabic Supplement Unicode range (U+0750 to U+077F)
 * Additional Arabic characters for African and Asian languages
 */
export const UNICODE_ARABIC_SUPPLEMENT = '\u0750-\u077F';

/**
 * Arabic Presentation Forms-A (U+FB50 to U+FDFF)
 * Contextual letter forms and ligatures
 */
export const UNICODE_ARABIC_PRESENTATION_A = '\uFB50-\uFDFF';

/**
 * Arabic Presentation Forms-B (U+FE70 to U+FEFF)
 * Additional contextual forms
 */
export const UNICODE_ARABIC_PRESENTATION_B = '\uFE70-\uFEFF';

/**
 * All Arabic Unicode ranges combined
 */
export const UNICODE_ARABIC_ALL =
  UNICODE_ARABIC_BASIC + UNICODE_ARABIC_SUPPLEMENT + UNICODE_ARABIC_PRESENTATION_A + UNICODE_ARABIC_PRESENTATION_B;

// =============================================================================
// RTL (Right-to-Left) Languages Combined
// =============================================================================

/**
 * RTL Unicode range for direction detection
 * Covers Hebrew, Arabic, Syriac, Thaana, NKo, Samaritan and related scripts
 * Note: This is a broader range used for text direction detection
 */
export const UNICODE_RTL_BROAD = '\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC';

// =============================================================================
// LTR (Left-to-Right) Languages
// =============================================================================

/**
 * Basic Latin letters (a-z, A-Z)
 */
export const UNICODE_LATIN_BASIC = 'A-Za-z';

/**
 * Extended Latin and other LTR scripts
 * Used primarily for text direction detection
 */
export const UNICODE_LTR_EXTENDED =
  '\u00C0-\u00D6\u00D8-\u00F6' + // Latin Extended-A
  '\u00F8-\u02B8' + // Latin Extended-B, IPA Extensions
  '\u0300-\u0590' + // Combining Diacritical Marks, Greek, Cyrillic
  '\u0800-\u1FFF' + // Various scripts
  '\u200E' + // Left-to-right mark
  '\u2C00-\uFB1C' + // Extended Latin, Greek Extended, etc.
  '\uFE00-\uFE6F\uFEFD-\uFFFF'; // Variation selectors, etc.

// =============================================================================
// Multilingual Search Validation - Character Class Groups
// =============================================================================
//
// Validated against production data (33K+ documents) using scripts/extract-unique-chars.js
//
// Security note: Characters like < > { } $ ` # @ ^ & are intentionally excluded
// to prevent injection attacks (XSS, NoSQL injection, template injection).
// =============================================================================

/** Core alphanumeric characters and whitespace */
const SEARCH_ALPHANUMERIC_CHAR_CLASS = 'a-zA-Z0-9\\s';

/**
 * ASCII punctuation found in production data.
 * Includes: - _ . ! " % ' ` ( ) * + , / : ; > ? ~ [ ] \ |
 */
const SEARCH_PUNCTUATION_CHAR_CLASS = '\\-_.!"%\'`()*+,/:;>?~' + '\\[\\]\\\\|'; // Brackets, backslash, pipe (escaped for regex)

/**
 * All Unicode dash and hyphen variants (U+2010-U+2015, U+2212, U+FE58, U+FE63, U+FF0D)
 */
const SEARCH_DASHES_CHAR_CLASS =
  '\u2010-\u2015' + // Hyphen, Non-breaking hyphen, Figure dash, En dash, Em dash, Horizontal bar
  '\u2212' + // Minus sign
  '\uFE58\uFE63\uFF0D'; // Small em dash, Small hyphen-minus, Fullwidth hyphen-minus

/**
 * Typographic characters: smart quotes, ellipsis, middle dot,
 * zero-width characters, non-breaking space, typographic spaces, variation selectors
 */
const SEARCH_TYPOGRAPHIC_CHAR_CLASS =
  '\u2018-\u201F' + // Typographic quotes: single/double open/close and variants
  '\u2026' + // Ellipsis
  '\u00B7' + // Middle dot
  '\u200B-\u200D' + // Zero-width space, zero-width non-joiner, zero-width joiner
  '\u00A0' + // Non-breaking space
  '\u2002-\u200A' + // Various typographic spaces (en space, em space, thin space, etc.)
  '\uFE00-\uFE0F'; // Variation selectors (used with emoji)

/**
 * Emoji via Unicode property escape (ES2018+, Node 10+).
 * Covers all standard emoji including future Unicode additions.
 * Requires the 'u' flag on the RegExp.
 */
const SEARCH_EMOJI_CHAR_CLASS = '\\p{Extended_Pictographic}';

/** Script ranges: Hebrew and Arabic (all forms) */
const SEARCH_SCRIPTS_CHAR_CLASS = UNICODE_HEBREW + UNICODE_ARABIC_ALL;

// =============================================================================
// Combined Search Character Class & Regex
// =============================================================================

/**
 * Combined character class for multilingual search input validation.
 * Built from individual groups for readability and maintainability.
 *
 * Used in:
 * - Zod validation (listLearningObjects.ts) - rejects invalid input
 * - DB sanitization (searchUtils.ts) - strips invalid chars as safety net
 */
export const MULTILINGUAL_SEARCH_CHAR_CLASS =
  SEARCH_ALPHANUMERIC_CHAR_CLASS +
  SEARCH_PUNCTUATION_CHAR_CLASS +
  SEARCH_DASHES_CHAR_CLASS +
  SEARCH_TYPOGRAPHIC_CHAR_CLASS +
  SEARCH_EMOJI_CHAR_CLASS +
  SEARCH_SCRIPTS_CHAR_CLASS;

/**
 * Regex pattern for validating multilingual search input.
 * Uses 'u' flag for proper Unicode support including supplementary plane characters (emoji).
 *
 * Allows: English, Hebrew, Arabic, numbers, whitespace, common punctuation,
 * all dash types, typographic characters, and emoji.
 *
 * Blocks: < > { } $ # @ ^ & (injection-prone characters)
 */
// eslint-disable-next-line no-misleading-character-class -- Variation selectors (U+FE00-FE0F) are intentionally included to support emoji
export const MULTILINGUAL_SEARCH_REGEX = new RegExp(`^[${MULTILINGUAL_SEARCH_CHAR_CLASS}]*$`, 'u');

export const hasForbiddenChars = (value: string | undefined): boolean =>
  value ? !MULTILINGUAL_SEARCH_REGEX.test(value) : false;
